import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Activity, Utensils, Weight } from "lucide-react";
import { PublicFacilityAnimal } from "@/hooks/use-facility-animals";
import { supabase } from "@/integrations/supabase/client";

interface VisibilityFlags {
  show_facility_animal_health: boolean;
  show_facility_animal_feeding: boolean;
  show_facility_animal_weight: boolean;
  show_facility_animal_photos: boolean;
}

interface PublicFacilityAnimalModalProps {
  animal: PublicFacilityAnimal | null;
  isOpen: boolean;
  onClose: () => void;
  visibilityFlags: VisibilityFlags;
}

const healthStatusColors: Record<string, string> = {
  'Healthy': 'bg-green-500/10 text-green-700 dark:text-green-400',
  'Monitoring': 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400',
  'Special care': 'bg-orange-500/10 text-orange-700 dark:text-orange-400',
  'Unknown': 'bg-gray-500/10 text-gray-700 dark:text-gray-400',
};

export const PublicFacilityAnimalModal: React.FC<PublicFacilityAnimalModalProps> = ({
  animal,
  isOpen,
  onClose,
  visibilityFlags,
}) => {
  if (!animal) return null;

  const getRelativeTime = (timestamp: string | null): string => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const daysDiff = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysDiff === 0) return 'Today';
    if (daysDiff === 1) return 'Yesterday';
    if (daysDiff < 7) return `${daysDiff} days ago`;
    if (daysDiff < 30) return `${Math.floor(daysDiff / 7)} weeks ago`;
    return `${Math.floor(daysDiff / 30)} months ago`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start gap-4">
            {visibilityFlags.show_facility_animal_photos && animal.photo_url && (
              <Avatar className="h-16 w-16">
                <AvatarImage 
                  src={supabase.storage.from('animal-photos').getPublicUrl(animal.photo_url).data.publicUrl} 
                  alt={animal.name} 
                />
                <AvatarFallback>
                  {animal.name?.charAt(0) || animal.species_common?.charAt(0) || '?'}
                </AvatarFallback>
              </Avatar>
            )}
            <div className="flex-1">
              <DialogTitle className="text-2xl">{animal.name}</DialogTitle>
              <div className="text-sm text-muted-foreground mt-1">
                {animal.species_common && <div className="font-medium">{animal.species_common}</div>}
                <div className="italic">{animal.species_scientific}</div>
              </div>
              <div className="flex gap-2 mt-2">
                {animal.sex && (
                  <Badge variant="outline" className="text-xs">{animal.sex}</Badge>
                )}
                <Badge variant="outline" className="text-xs">{animal.status}</Badge>
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Health Status Block */}
          {visibilityFlags.show_facility_animal_health && (
            <>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-semibold">Health Status</h3>
                </div>
                <div className="space-y-2 pl-6">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Status:</span>
                    <Badge 
                      variant="outline" 
                      className={healthStatusColors[animal.public_health_status] || healthStatusColors['Unknown']}
                    >
                      {animal.public_health_status}
                    </Badge>
                  </div>
                  {animal.public_health_last_updated && (
                    <div className="text-sm text-muted-foreground">
                      Last updated: {getRelativeTime(animal.public_health_last_updated)}
                    </div>
                  )}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Feeding Block */}
          {visibilityFlags.show_facility_animal_feeding && (
            <>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Utensils className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-semibold">Feeding & Diet</h3>
                </div>
                <div className="space-y-2 pl-6">
                  <div className="text-sm">
                    <span className="text-muted-foreground">Schedule: </span>
                    <span>{animal.public_feeding_summary}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-foreground">Diet: </span>
                    <span>{animal.public_diet_label}</span>
                  </div>
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Weight Block */}
          {visibilityFlags.show_facility_animal_weight && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Weight className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold">Weight</h3>
              </div>
              <div className="text-sm pl-6">
                {animal.public_weight_summary}
              </div>
            </div>
          )}

          {/* Info message */}
          <div className="text-xs text-muted-foreground pt-4 border-t">
            This is a read-only public view of animal information. 
            For detailed records, please contact the facility directly.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
